import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { trackPageView } from './utils/analytics'
import { ProtectedRoute } from './components/ProtectedRoute'
import { GuestRoute } from './components/GuestRoute'
import { MainLayout } from './components/MainLayout'
import { Toaster } from '@/components/ui/sonner'

const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'))
const PortfolioDashboardPage = lazy(() => import('./pages/PortfolioDashboardPage'))
const NewPortfolioPage = lazy(() => import('./pages/NewPortfolioPage'))
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'))
const PortfolioSettingsPage = lazy(() => import('./pages/PortfolioSettingsPage'))
const PortfolioAnalyticsPage = lazy(() => import('./pages/PortfolioAnalyticsPage'))
const TaxSummaryPage = lazy(() => import('./pages/TaxSummaryPage'))
const RisksPage = lazy(() => import('./pages/RisksPage'))
const ScenarioDetailPage = lazy(() => import('./pages/ScenarioDetailPage'))
const CrashSimulatorPage = lazy(() => import('./pages/CrashSimulatorPage'))
const RiskChecksPage = lazy(() => import('./pages/RiskChecksPage'))
const NewRiskCheckPage = lazy(() => import('./pages/NewRiskCheckPage'))
const RiskCheckDetailPage = lazy(() => import('./pages/RiskCheckDetailPage'))
const PublicRiskCheckPage = lazy(() => import('./pages/PublicRiskCheckPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'))

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <PageTracker />
      <Suspense>
      <Routes>
        {/* Auth routes - only accessible when not logged in */}
        <Route path="/login" element={<GuestRoute><SignInPage /></GuestRoute>} />

        <Route path="/signup" element={<GuestRoute><SignUpPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
        <Route path="/confirm-email" element={<ConfirmEmailPage />} />

        {/* Public risk check — no auth required */}
        <Route path="/check/:shareToken" element={<PublicRiskCheckPage />} />

        {/* Protected routes with persistent layout */}
        <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/portfolio/new" element={<NewPortfolioPage />} />
          <Route path="/portfolio/:slug" element={<PortfolioPage />} />
          <Route path="/portfolio/:slug/dashboard" element={<PortfolioDashboardPage />} />
          <Route path="/portfolio/:slug/transactions" element={<TransactionsPage />} />
          <Route path="/portfolio/:slug/analytics" element={<PortfolioAnalyticsPage />} />
          <Route path="/portfolio/:slug/analytics/taxes" element={<TaxSummaryPage />} />
          <Route path="/portfolio/:slug/risks" element={<RisksPage />} />
          <Route path="/portfolio/:slug/risks/simulator" element={<CrashSimulatorPage />} />
          <Route path="/portfolio/:slug/risks/:scenarioName" element={<ScenarioDetailPage />} />
          <Route path="/portfolio/:slug/settings" element={<PortfolioSettingsPage />} />
          <Route path="/risk-checks" element={<RiskChecksPage />} />
          <Route path="/risk-checks/new" element={<NewRiskCheckPage />} />
          <Route path="/risk-checks/:id" element={<RiskCheckDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/api-keys" element={<ApiKeysPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
  )
}

export default App
